import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, computed, DestroyRef, effect, inject, Input, input, linkedSignal, signal, untracked, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader, type Sort } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { RouterLink } from '@angular/router';
import { fileUtils, SocketEventNamespace } from '@core';
import { IncidentColumn, incidentColumnTitle, IncidentStatus, type IncidentTabStatusFilter, type NormalizedIncident } from '@models';
import { SocketEvents } from '@ngrx-store';
import { CdkVirtualScrollViewportWrapperComponent, NoDataPlaceholderComponent, TableZoomComponent } from '@ui';
import { filter } from 'rxjs';

import { SortItemDto } from '../../../models';
import { IncidentsCriteriaStore, type IncidentsFilterCriteria } from '../../../state/incidents-criteria.store';
import { IncidentSeverityIconComponent } from '../../incident-status';
import { NotesDialogComponent } from '../../notes-dialog';
import { IncidentsV2TableStore } from './incidents-v2-table.store';
import { normalizeIncidentsV2Grid } from './normalize-incidents-v2-grid';

const ROW_HEIGHT = 44;
// how close (in px) to the bottom of the scroll viewport before the next page
// loads. Measured off the viewport's native scrollTop/scrollHeight rather than
// `CdkVirtualScrollViewport.getRenderedRange()` — the table body is a plain
// `mat-table` (no `*cdkVirtualFor`), so the viewport is never "attached" and
// its rendered-range bookkeeping stays stuck at {0,0} forever.
const SCROLL_THRESHOLD_PX = 200;

export const staticIncidentV2ColumnTitle: Record<string, string> = {
  [IncidentColumn.INDEX]: '№',
  [IncidentColumn.TICKET_NUMBER]: 'Ticket №',
};

@Component({
  imports: [
    MatTable,
    MatSortHeader,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatCellDef,
    MatSort,
    RouterLink,
    MatHeaderCellDef,
    NgClass,
    NoDataPlaceholderComponent,
    MatHeaderRow,
    MatRow,
    MatRowDef,
    MatHeaderRowDef,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    TableZoomComponent,
    MatIcon,
    CdkVirtualScrollViewportWrapperComponent,
    IncidentSeverityIconComponent,
    MatProgressSpinner,
  ],
  providers: [IncidentsV2TableStore],
  selector: 'app-incidents-v2-table',
  styleUrls: ['./incidents-v2-table.component.scss'],
  templateUrl: './incidents-v2-table.component.html',
})
export class IncidentsV2TableComponent implements AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;
  @Input() noDataText = 'Data is Empty';
  @Input() noResolvedIncidentsText = 'No resolved incidents for selected period';
  @Input() isNoDataBigSize = false;
  @Input() isMobile = false;
  @Input() tableId = '';
  criteria = input.required<IncidentsFilterCriteria>();
  tab = input.required<IncidentTabStatusFilter>();
  sortCriteria = input<SortItemDto | null>(null);
  @Input() columns: string[] | null = null;

  dataSource = new MatTableDataSource<NormalizedIncident>();
  dataSourceIndex: { [id: string]: number; } = {};
  public store = inject(IncidentsV2TableStore);
  public criteriaStore = inject(IncidentsCriteriaStore);
  readonly staticIncidentColumnTitle = staticIncidentV2ColumnTitle;
  readonly incidentColumnTitle = incidentColumnTitle;
  readonly IncidentColumn = IncidentColumn;

  protected zoom = signal<string | null>(null);
  protected TYPICAL_COL_HEIGHT = ROW_HEIGHT;
  protected readonly eIncidentStatus = IncidentStatus;
  protected isInitialLoad = signal<boolean>(true);
  protected isTabSwitching = signal<boolean>(false);

  protected showSpinner = computed(() => {
    return this.store.isLoading() && (this.isInitialLoad() || this.isTabSwitching());
  });

  private changeDetectionRef = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private activeSortCriteria = linkedSignal(() => this.sortCriteria());

  constructor() {
    effect(() => {
      const incidents = normalizeIncidentsV2Grid(this.store.rows());

      this.initTableData(incidents);
    });

    let previousTab: IncidentTabStatusFilter | undefined;

    effect(() => {
      const criteria = this.criteria();
      const tab = this.tab();
      const sort = this.activeSortCriteria();

      if (previousTab !== undefined && previousTab !== tab) {
        this.isTabSwitching.set(true);
      }

      previousTab = tab;

      untracked(() => this.store.setQuery({ criteria, sort, tab }));
    });

    effect(() => {
      if (!this.store.isLoading()) {
        this.isInitialLoad.set(false);
        this.isTabSwitching.set(false);
      }
    });

    // Real-time incident create/resolve push — old `IncidentsTableComponent`
    // called `incidentsFilterService.refreshIncidentCounter()` here (that
    // service's own `incidentsData` mirror, unrelated to this store); the v2
    // equivalent is just refetching the current page so a live-created
    // incident shows up.
    SocketEvents
      .pipe(
        filter(({ type }) => type.startsWith(SocketEventNamespace.INCIDENTS)),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.store.reload());
  }

  ngAfterViewInit() {
    this.sort.sortChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sort: Sort) => {
        this.activeSortCriteria.set(sort.direction ? { field: sort.active, order: sort.direction } : null);
      });

    this.changeDetectionRef.detectChanges();

    this.viewport?.elementScrolled()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onScroll());
  }

  getTableIndexes(incidents: NormalizedIncident[]) {
    this.dataSourceIndex = {};
    for (let i = 0; i < incidents.length; i++) {
      this.dataSourceIndex[incidents[i].id] = i + 1;
    }
  }

  initTableData(incidents: NormalizedIncident[]) {
    this.dataSource.data = incidents;
    this.getTableIndexes(incidents);
  }

  prepareIncidentsDataForDownloading() {
    const columns = (this.columns || this.criteriaStore.displayedColumns()).map(column => (column === 'index' ? 'id' : column));

    return fileUtils.prepareDataForDownloading(columns, this.dataSource.filteredData);
  }

  exportIncidentsToCSV() {
    fileUtils.exportDataToCSV(this.prepareIncidentsDataForDownloading(), 'incidents report');
  }

  exportIncidentsToJSON() {
    fileUtils.exportDataToJSON(this.prepareIncidentsDataForDownloading(), 'incidents report');
  }

  protected onScroll() {
    const element = this.viewport?.elementRef.nativeElement;

    if (!element || !this.dataSource.data.length) {
      return;
    }

    const { clientHeight, scrollHeight, scrollTop } = element;

    if (scrollHeight - (scrollTop + clientHeight) <= SCROLL_THRESHOLD_PX) {
      this.store.loadNextPage();
    }
  }

  protected openNotesDialog(incident: NormalizedIncident) {
    this.dialog.open(NotesDialogComponent, {
      backdropClass: 'backdrop-modal-panel',
      data: { incident },
    });
  }

  protected trackBy(_index: number, el: NormalizedIncident) {
    return el.id;
  }
}
